import type {
  ShopperAiTagGroupId,
  ShopperAiTagId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  TagGroupResponse,
  TagResponse,
} from "@redotech/effect-api-shared/rpc/tag";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import type { RedoBadgeColor } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { useLiveQuery } from "@tanstack/react-db";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";

const pillThemeToBadgeColor: Record<PillTheme, RedoBadgeColor> = {
  [PillTheme.NORMAL]: "gray",
  [PillTheme.PRIMARY_LIGHT]: "purple",
  [PillTheme.LIGHT_PINK]: "pink",
  [PillTheme.DANGER]: "orange",
  [PillTheme.NEUTRAL_YELLOW]: "warning",
  [PillTheme.SUCCESS]: "success",
  [PillTheme.NEUTRAL_BLUE]: "blueLight",
  [PillTheme.PRIMARY]: "purple",
  [PillTheme.SOLID_PINK]: "pink",
  [PillTheme.SOLID_RED]: "error",
  [PillTheme.SOLID_ORANGE]: "orange",
  [PillTheme.SOLID_YELLOW]: "warning",
  [PillTheme.SOLID_GREEN]: "success",
  [PillTheme.SOLID_LIGHT_BLUE]: "blue",
  [PillTheme.SOLID_NAVY]: "indigo",
  [PillTheme.SOLID_BLACK]: "black",
  [PillTheme.SOLID_WHITE]: "white",
  [PillTheme.BRAND]: "brand",
  [PillTheme.GRAY_BLUE]: "grayBlue",
  [PillTheme.WHITE_SUCCESS]: "whiteSuccess",
  [PillTheme.GRAY_ALTERNATE]: "gray",
  [PillTheme.BRAND_ALTERNATE]: "brand",
  [PillTheme.ERROR_ALTERNATE]: "error",
  [PillTheme.WARNING_ALTERNATE]: "warning",
  [PillTheme.SUCCESS_ALTERNATE]: "success",
  [PillTheme.GRAY_BLUE_ALTERNATE]: "grayBlue",
  [PillTheme.BLUE_LIGHT_ALTERNATE]: "blueLight",
  [PillTheme.BLUE_ALTERNATE]: "blue",
  [PillTheme.INDIGO_ALTERNATE]: "indigo",
  [PillTheme.PURPLE_ALTERNATE]: "purple",
  [PillTheme.PINK_ALTERNATE]: "pink",
  [PillTheme.ORANGE_ALTERNATE]: "orange",
  [PillTheme.BLACK_ALTERNATE]: "black",
  [PillTheme.WHITE_ALTERNATE]: "white",
  [PillTheme.WHITE_SUCCESS_ALTERNATE]: "whiteSuccess",
};

const tagSearcher = new Fuse<TagResponse>([], {
  keys: ["name"],
  threshold: 0.3,
});

export interface UseTagSelectorResult {
  tagListItems: RedoListItem<TagResponse>[];
  selectedTagIds: readonly ShopperAiTagId[];
  canEditTags: boolean;
  loading: boolean;

  handleTagToggled: (tag: TagResponse) => void;

  searchText: string;
  setSearchText: (text: string) => void;
}

function buildGroupedTagListItems(
  tags: readonly TagResponse[],
  tagGroups: readonly TagGroupResponse[],
): RedoListItem<TagResponse>[] {
  const groupMap = new Map<ShopperAiTagGroupId, TagGroupResponse>();
  for (const group of tagGroups) {
    groupMap.set(group.id, group);
  }

  const tagsByGroup = new Map<ShopperAiTagGroupId, TagResponse[]>();
  for (const tag of tags) {
    const existing = tagsByGroup.get(tag.tagGroupId);
    if (existing) {
      existing.push(tag);
    } else {
      tagsByGroup.set(tag.tagGroupId, [tag]);
    }
  }

  const items: RedoListItem<TagResponse>[] = [];

  for (const group of tagGroups) {
    const groupTags = tagsByGroup.get(group.id);
    if (!groupTags || groupTags.length === 0) {
      continue;
    }
    items.push({
      type: "section" as const,
      id: `group-${group.id}`,
      text: group.name,
      value: undefined as unknown as TagResponse,
    });
    for (const tag of groupTags) {
      items.push({
        type: "badge" as const,
        id: tag.id,
        badge: {
          color: pillThemeToBadgeColor[tag.pillTheme ?? PillTheme.NORMAL],
          text: tag.name,
        },
        selectionVariant: group.allowMultipleTags ? "checkbox" : "none",
        value: tag,
      });
    }
  }

  return items;
}

export function useTagSelector({
  currentTagIds,
  onTagsChanged,
}: {
  currentTagIds: readonly ShopperAiTagId[];
  onTagsChanged: (newTagIds: ShopperAiTagId[]) => void;
}): UseTagSelectorResult {
  const collections = useGlobalConversationCollections();
  const tagsResult = useLiveQuery((q) => q.from({ tag: collections.tags }), []);
  const tagGroupsResult = useLiveQuery(
    (q) => q.from({ tagGroup: collections.tagGroups }),
    [],
  );
  const tags = tagsResult.data;
  const tagGroups = tagGroupsResult.data;
  const loading = tagsResult.isLoading || tagGroupsResult.isLoading;

  const [searchText, setSearchText] = useState("");
  const canEditTags = useHasPermission(SupportPermissions.MANAGE_TAG);

  const tagsArray = useMemo(() => [...tags], [tags]);
  const filteredTags = useSearch<TagResponse>(
    tagSearcher,
    tagsArray,
    searchText,
  );

  const tagGroupMap = useMemo(() => {
    const map = new Map<ShopperAiTagGroupId, TagGroupResponse>();
    for (const group of tagGroups) {
      map.set(group.id, group);
    }
    return map;
  }, [tagGroups]);

  const tagListItems: RedoListItem<TagResponse>[] = useMemo(() => {
    if (searchText) {
      return filteredTags.map((tag) => ({
        type: "badge" as const,
        id: tag.id,
        badge: {
          color: pillThemeToBadgeColor[tag.pillTheme ?? PillTheme.NORMAL],
          text: tag.name,
        },
        value: tag,
      }));
    }
    return buildGroupedTagListItems(filteredTags, tagGroups);
  }, [filteredTags, tagGroups, searchText]);

  const tagsByGroup = useMemo(() => {
    const map = new Map<ShopperAiTagGroupId, TagResponse[]>();
    for (const tag of tags) {
      const existing = map.get(tag.tagGroupId);
      if (existing) {
        existing.push(tag);
      } else {
        map.set(tag.tagGroupId, [tag]);
      }
    }
    return map;
  }, [tags]);

  const handleTagToggled = useHandler((tag: TagResponse) => {
    const currentSet = new Set(currentTagIds);
    if (currentSet.has(tag.id)) {
      currentSet.delete(tag.id);
    } else {
      const group = tagGroupMap.get(tag.tagGroupId);
      //if the group does not allow multiple tags, remove any existing tags from that group
      if (group && !group.allowMultipleTags) {
        const groupTags = tagsByGroup.get(tag.tagGroupId) ?? [];
        for (const groupTag of groupTags) {
          currentSet.delete(groupTag.id);
        }
      }
      currentSet.add(tag.id);
    }
    const newTagIds = [...currentSet];
    onTagsChanged(newTagIds);
  });

  return {
    tagListItems,
    selectedTagIds: currentTagIds,
    canEditTags,
    loading,
    handleTagToggled,
    searchText,
    setSearchText,
  };
}
